"use client"

import { io, Socket } from "socket.io-client"
import { useDemoStore } from "@/store/demoStore"

// Static demo repository info
const DEMO_OWNER = "axios"
const DEMO_REPO = "axios"
const DEMO_BRANCH = "main"

class DemoApi {
  private socket: Socket | null = null
  private store = useDemoStore.getState()

  connect(fingerprint: string | null): void {
    // If socket already exists and is connected, don't create a new one
    if (this.socket && this.socket.connected) {
      return
    }
    
    // If socket exists but is not connected, disconnect it first
    if (this.socket) {
      this.socket.disconnect()
      this.socket = null
    }
    
    const socketUrl = process.env.NEXT_PUBLIC_SOCKET_URL
    if (!socketUrl) {
      this.store.setConnectionState(false, false, "Socket URL not configured")
      return
    }

    // Get fresh store state
    this.store = useDemoStore.getState()
    this.store.setConnectionState(false, true, null)

    // Normalize socket URL (remove trailing slash, like frontend does)
    const normalizedUrl = socketUrl.replace(/\/$/, "")

    // Create socket connection with fingerprint as query parameter
    const query: Record<string, string> = {}
    if (fingerprint) {
      query.fingerprint = fingerprint
    }

    this.socket = io(normalizedUrl, {
      transports: ["websocket", "polling"], // Match frontend: websocket first
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
      timeout: 10000,
      query,
      extraHeaders: {
        "ngrok-skip-browser-warning": "true", // Required for ngrok
      },
    })

    this.setupEventListeners()
  }

  disconnect(): void {
    if (this.socket) {
      this.socket.disconnect()
      this.socket = null
    }
    this.store = useDemoStore.getState()
    this.store.setConnectionState(false, false, null)
  }

  sendMessage(question: string, paths: string[], history: Array<{ role: string; content: string }>): void {
    // Get fresh store state first
    this.store = useDemoStore.getState()
    
    // Check socket connection - use socket.connected property as primary check
    // The store state might be stale, but socket.connected is always current
    if (!this.socket || !this.socket.connected) {
      this.store.setConnectionState(false, false, "Not connected to server")
      return
    }

    // Add user message to store with attachments
    this.store.addMessage({
      id: `msg-${Date.now()}-${Math.random()}`,
      role: "user",
      content: question,
      timestamp: new Date(),
      attachments: paths.length > 0 ? paths.map((path) => ({
        id: `att-${Date.now()}-${Math.random()}`,
        name: path.split('/').pop() || path,
        path,
      })) : undefined,
    })

    // Emit message with required format
    this.socket.emit("ask", {
      owner: DEMO_OWNER,
      repo: DEMO_REPO,
      branch: DEMO_BRANCH,
      question,
      paths,
      history,
    })
  }

  private setupEventListeners(): void {
    if (!this.socket) return

    // Connection events
    this.socket.on("connect", () => {
      this.store = useDemoStore.getState()
      this.store.setConnectionState(true, false, null)
    })

    this.socket.on("disconnect", (reason) => {
      this.store = useDemoStore.getState()
      this.store.setConnectionState(false, false, null)
      if (reason === "io server disconnect") {
        // Server disconnected, try to reconnect
        this.socket?.connect()
      }
    })

    this.socket.on("connect_error", (err) => {
      this.store = useDemoStore.getState()
      this.store.setConnectionState(false, false, `Connection failed: ${err.message}`)
    })

    this.socket.on("reconnect", () => {
      this.store = useDemoStore.getState()
      this.store.setConnectionState(true, false, null)
    })

    this.socket.on("reconnect_error", (err) => {
      this.store = useDemoStore.getState()
      this.store.setConnectionState(false, false, `Reconnection failed: ${err.message}`)
    })

    // Chat events
    this.socket.on("ask", (data: any) => {
      // Question received confirmation
      console.log("Question received:", data)
    })

    this.socket.on("answer", (data: { answer: string; question?: string }) => {
      this.store = useDemoStore.getState()
      // Complete answer received - update last message or create new
      const messages = [...this.store.messages]
      const lastMessage = messages[messages.length - 1]
      if (lastMessage && lastMessage.role === "assistant" && lastMessage.isStreaming) {
        // Update existing streaming message using store action
        this.store.updateMessage(lastMessage.id, {
          content: data.answer || lastMessage.content,
          isStreaming: false,
        })
      } else {
        // Create new message
        this.store.addMessage({
          id: `msg-${Date.now()}-${Math.random()}`,
          role: "assistant",
          content: data.answer || "",
          timestamp: new Date(),
          isStreaming: false,
        })
      }
    })

    this.socket.on("answer.chunk", (data: { content: string; question?: string }) => {
      this.store = useDemoStore.getState()
      // Streaming answer chunk - data.content contains the chunk text
      this.store.updateLastMessage(data.content)
    })

    this.socket.on("message.created", (data: { chatId?: string; content: string; role?: string }) => {
      this.store = useDemoStore.getState()
      
      // Check if this is a user message - if so, ignore it since we already added it in sendMessage
      const isUserMessage = data.role === "USER" || data.role === "user"
      
      if (isUserMessage) {
        // User message - we already added it in sendMessage, so ignore this event
        return
      }
      
      // If role is missing or undefined, check if content matches the last user message
      // This handles cases where the server doesn't send role field
      if (!data.role) {
        const lastMessage = this.store.messages[this.store.messages.length - 1]
        if (lastMessage && lastMessage.role === "user" && lastMessage.content === data.content) {
          return
        }
      }
      
      // This is an assistant message - update last streaming message with full content and mark as complete
      const messages = [...this.store.messages]
      const lastMessage = messages[messages.length - 1]
      
      if (lastMessage && lastMessage.role === "assistant") {
        // Update existing assistant message with full content and stop streaming using store action
        this.store.updateMessage(lastMessage.id, {
          content: data.content || lastMessage.content,
          isStreaming: false,
        })
      } else {
        // Create new message if none exists
        this.store.addMessage({
          id: `msg-${Date.now()}-${Math.random()}`,
          role: "assistant",
          content: data.content || "",
          timestamp: new Date(),
          isStreaming: false,
        })
      }
    })

    this.socket.on("ai.thinking", (data: any) => {
      // AI is processing - create empty assistant message to show thinking
      this.store = useDemoStore.getState()
      const messages = [...this.store.messages]
      const lastMessage = messages[messages.length - 1]
      // Only add thinking message if last message is not already an empty assistant message
      if (!lastMessage || lastMessage.role !== "assistant" || lastMessage.content.trim()) {
        this.store.addMessage({
          id: `msg-thinking-${Date.now()}`,
          role: "assistant",
          content: "",
          timestamp: new Date(),
          isStreaming: false,
        })
      }
    })

    this.socket.on("error", (data: { message?: string }) => {
      this.store = useDemoStore.getState()
      this.store.setConnectionState(
        this.store.isConnected,
        false,
        data.message || "An error occurred"
      )
    })

    // Rate limit events
    this.socket.on("exception", (data: { type?: string; message?: string; timeLeft?: number; resetTime?: string; maxLimit?: number }) => {
      this.store = useDemoStore.getState()
      
      // Check if this is a daily limit exception
      if (data.type === "DAILY_LIMIT") {
        // Try to extract limit from message if not provided directly
        // Message format: "Daily limit reached (10 messages). Resets at 1:16:43 PM."
        let extractedLimit: number | undefined = data.maxLimit
        if (extractedLimit === undefined && data.message) {
          const match = data.message.match(/\((\d+)\s+messages?\)/i)
          if (match) {
            extractedLimit = parseInt(match[1], 10)
          }
        }
        
        // Set daily limit if we found it
        if (extractedLimit !== undefined) {
          this.store.setDailyLimit(extractedLimit)
        }
        
        this.store.setLimitReached(
          data.message || "Daily limit reached",
          data.resetTime
        )
      }
    })

  }

  getSocket(): Socket | null {
    return this.socket
  }

  // Fetch repository tree
  // For local development when backend is down, prioritize mock data
  async fetchTree(): Promise<any> {
    // Always use mock data for local development when backend is down
    // Uncomment the line below to force mock data, or keep API fallback for production
    // return this.getMockTreeData()

    const apiUrl = process.env.NEXT_PUBLIC_API_URL
    if (!apiUrl) {
      // No API URL configured - use mock data
      console.warn('API URL not configured, using mock axios repository tree for demo')
      return this.getMockTreeData()
    }

    // Normalize API URL: remove trailing slash to avoid double slashes
    const normalizedApiUrl = apiUrl.replace(/\/+$/, '')
    const fetchUrl = `${normalizedApiUrl}/explorer/tree`
    
    let response: Response
    try {
      response = await fetch(fetchUrl, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'ngrok-skip-browser-warning': 'true',
        },
      })
    } catch (fetchError) {
      // API fetch failed - use mock data for demo
      // Only log in development to avoid console errors in Lighthouse
      if (process.env.NODE_ENV === 'development') {
        console.warn('Failed to fetch tree from API, using mock axios repository tree for demo', fetchError)
      }
      return this.getMockTreeData()
    }

    if (!response.ok) {
      // API returned error - use mock data for demo
      // Only log in development to avoid console errors in Lighthouse
      if (process.env.NODE_ENV === 'development') {
        console.warn(`API returned ${response.statusText}, using mock axios repository tree for demo`)
      }
      return this.getMockTreeData()
    }

    // Check Content-Type before parsing JSON
    const contentType = response.headers.get('content-type') || ''
    if (!contentType.includes('application/json')) {
      // Endpoint doesn't exist or returned HTML - use mock data for demo
      // Only log in development to avoid console errors in Lighthouse
      if (process.env.NODE_ENV === 'development') {
        console.warn(`Demo endpoint returned ${contentType}, using mock axios repository tree for demo`)
      }
      return this.getMockTreeData()
    }

    try {
      const responseData = await response.json()
      
      // Transform API response from { success, msg, data: [...] } to { tree: { children: [...] } }
      // This matches what useTreeConversion expects
      if (responseData.success && Array.isArray(responseData.data)) {
        return {
          tree: {
            children: responseData.data,
          },
        }
      }
      
      // Fallback: if already in correct format or mock data format
      if (responseData.tree) {
        return responseData
      }
      
      // If data is directly an array, wrap it
      if (Array.isArray(responseData.data)) {
        return {
          tree: {
            children: responseData.data,
          },
        }
      }
      
      return responseData
    } catch (jsonError) {
      // JSON parsing failed - use mock data for demo
      // Only log in development to avoid console errors in Lighthouse
      if (process.env.NODE_ENV === 'development') {
        console.warn('Failed to parse API response, using mock axios repository tree for demo', jsonError)
      }
      return this.getMockTreeData()
    }
  }

  // Mock tree data for demo fallback (transformed to match useTreeConversion format)
  // Realistic axios repository structure for local development when backend is down
  private getMockTreeData(): any {
    return {
      tree: {
        children: [

        ],
      },
    }
  }
}

// Export singleton instance
export const demoApi = new DemoApi()

